import { useLanguage } from "@/lib/LanguageContext/LanguageContext.tsx";
import { socialLinks } from "@/lib/Data/personalData.ts";
import { faGithub, faLinkedin } from "@fortawesome/free-brands-svg-icons";
import { faPaperPlane } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { FormEvent } from "react";
import { useFormStatus, useIntersectionObserver } from "../../hooks";
import styles from "./Contact.module.scss";

const Contact: React.FC = () => {
  const { setStatus, isLoading, isSuccess, isError } = useFormStatus();
  const { t } = useLanguage();
  const sectionRef = useIntersectionObserver();

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setStatus("sending");

    const form = e.currentTarget;
    const formData = new FormData(form);
    const accessKey = import.meta.env.VITE_MAIL_SERVICE;

    // Validate access key
    if (!accessKey) {
      console.error("Missing access key");
      setStatus("error");
      return;
    }

    // Ensure access key is included
    formData.set("access_key", accessKey);
    formData.append("from_name", formData.get("name") as string);

    try {
      const response = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: {
          Accept: "application/json",
        },
        body: formData,
      });

      const data = await response.json();
      console.log("Response:", data); // Add this for debugging

      if (data.success) {
        setStatus("success");
        form.reset();
      } else {
        setStatus("error");
        console.error("Form submission failed:", data);
      }
    } catch (error) {
      setStatus("error");
      console.error("Form submission error:", error);
    }

    // The useFormStatus hook handles auto-reset
  };

  return (
    <section ref={sectionRef} id="contact" className={styles.contact}>
      <div className={styles.contactContainer}>
        <h2 className={`${styles.contactTitle} fade-up-section`}>
          {t("contact")}
        </h2>
        <div className={styles.contactWrapper}>
          <div className={styles.contactContent}>
            <form
              onSubmit={handleSubmit}
              className={`${styles.contactForm} fade-up-section`}
            >
              <input
                type="hidden"
                name="access_key"
                value={import.meta.env.VITE_MAIL_SERVICE}
              />
              <input
                type="hidden"
                name="subject"
                value="New contact form submission from 3d-space-portfolio."
              />
              <input
                type="checkbox"
                name="botcheck"
                className={styles.botcheck}
                style={{ display: "none" }}
              />

              <div className={styles.formGroup}>
                <label htmlFor="name">
                  {t("yourName")}{" "}
                  <span className={styles.required} aria-label="required">
                    *
                  </span>
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  required
                  aria-required="true"
                  aria-invalid={isError ? "true" : "false"}
                />
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="email">
                  {t("email")}{" "}
                  <span className={styles.required} aria-label="required">
                    *
                  </span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  required
                  aria-required="true"
                  aria-invalid={isError ? "true" : "false"}
                />
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="message">
                  {t("message")}{" "}
                  <span className={styles.required} aria-label="required">
                    *
                  </span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  required
                  rows={5}
                  aria-required="true"
                  aria-invalid={isError ? "true" : "false"}
                  aria-describedby="message-help"
                />
                <div id="message-help" className="sr-only">
                  <span className="sr-only">
                    {t("srOnlyMessagePlaceholder")}
                  </span>
                </div>
              </div>

              <div className={styles.formActions}>
                <div className={styles.socialLinks}>
                  <a
                    href={socialLinks.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.socialLink}
                    aria-label="Connect on LinkedIn"
                  >
                    <FontAwesomeIcon icon={faLinkedin} />
                  </a>
                  <a
                    href={socialLinks.github}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.socialLink}
                    aria-label="View GitHub projects"
                  >
                    <FontAwesomeIcon icon={faGithub} />
                  </a>
                </div>
                <button
                  type="submit"
                  className={styles.submitBtn}
                  disabled={isLoading}
                >
                  {isLoading ? t("sending") : t("sendMessage")}
                  <FontAwesomeIcon
                    icon={faPaperPlane}
                    className={styles.buttonIcon}
                  />
                </button>
              </div>

              {isSuccess && (
                <p
                  className={`${styles.statusMessage} ${styles.success}`}
                  role="status"
                  aria-live="polite"
                >
                  {t("messageSent")}
                </p>
              )}
              {isError && (
                <p
                  className={`${styles.statusMessage} ${styles.error}`}
                  role="alert"
                  aria-live="assertive"
                >
                  {t("errorSendingMessage")}
                </p>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
